/**
 * Broker REM and Board pack: the wire contract and the pure helpers both tabs share.
 *
 * A pure module with NO imports, for the reason reportContract.ts gives: the hooks reach
 * `lib/firebase` at module load, so anything a test needs to assert lives where it can be imported
 * without a live client. Kept beside reportContract.ts rather than inside it so this phase adds a
 * file instead of editing a shared one.
 *
 * The action strings are the Zod literals on the server. A mismatch is not a type error on either
 * side: it is a runtime "invalid-argument" from a callable that looks correct in both files.
 *
 * MONEY IS INTEGER CENTS on the wire and in every value this module computes.
 */

/** On pipelineTrackerReportAction. */
export const BROKER_MONTHLY_REPORT_ACTION = 'brokerMonthlyReport';
/** On pipelineTrackerTaxonomyAction, the tracker's settings callable. */
export const GET_REM_SETTINGS_ACTION = 'getRemSettings';
export const SAVE_REM_SETTINGS_ACTION = 'saveRemSettings';

/**
 * Where the commission figures live. They are OUTSIDE the tracker isolation fence (BigQuery
 * commission tables), so the tracker links to the VALUTTEN page that owns them rather than
 * querying them. Paths mirror routes/AppRoutes.tsx; the Broker Report is reached per broker from
 * Broker Loan Books, so that is the page linked.
 */
export const COMMISSION_LINKS = {
  commissionSettings: { to: '/broker-commission-settings', label: 'Commission Settings' },
  brokerReport: { to: '/reports/broker-loan-books', label: 'Reports › Broker Report' },
  commissionBreakdown: { to: '/commission-breakdown', label: 'Commission Breakdown' },
} as const;

// ---------------------------------------------------------------- wire types

export interface RemBand {
  /** Inclusive lower bound, integer cents. */
  fromCents: number;
  /** Exclusive upper bound, integer cents. Null is the open top band. */
  toCents: number | null;
  /** The broker's split of their own net commission for the month, 0 to 100. */
  ratePct: number;
}

export interface RemBrokerCost {
  monthlyBaseCents?: number;
  monthlySupportCents?: number;
}

export interface RemSettings {
  version: number;
  updatedAt: string;
  updatedBy: string;
  bands: RemBand[];
  brokerCosts: Record<string, RemBrokerCost>;
}

export type BandReached =
  | { status: 'in_band'; index: number; fromCents: number; toCents: number | null; ratePct: number }
  | { status: 'no_bands' }
  | { status: 'above_top' }
  | { status: 'negative_volume' };

/** A month's band, or 'not_banded' for someone the roster does not list as a broker. */
export type MonthBand = BandReached | { status: 'not_banded' };

/**
 * How the broker roster classifies a person (mirrors BrokerMonthsKind on the server). Only
 * 'broker' is banded; 'unassigned' is the bucket for deals with no owner, not a person.
 */
export type BrokerMonthsKind = 'broker' | 'group' | 'support' | 'unassigned';

export interface Flow {
  count: number;
  valueCents: number;
}

export interface MonthWindow {
  month: string;
  from: string;
  to: string;
  partial: boolean;
}

export interface BrokerMonthFigures {
  month: string;
  settled: Flow;
  submissions: Flow;
  settledMissingValue: number;
  band: MonthBand;
}

export interface BrokerMonthsRow {
  /** '' for the Unassigned bucket: read `kind`, never the key, to know what a row is. */
  adviserKey: string;
  adviserName: string;
  kind: BrokerMonthsKind;
  groupLabel: string | null;
  months: BrokerMonthFigures[];
}

export interface LoanTypeRow {
  label: string;
  basis: 'recorded' | 'mixed' | 'not_recorded' | 'no_split';
  count: number;
  valueCents: number;
}

export type LoanTypeBreakdown =
  | {
      status: 'ok';
      salesAsAtDate: string;
      from: string;
      to: string;
      settledDeals: number;
      settledValueCents: number;
      byPurpose: LoanTypeRow[];
      byTransactionType: LoanTypeRow[];
    }
  | { status: 'no_sales_snapshot'; from: string; to: string };

export interface BrokerMonthlyPayload {
  asAtDate: string;
  months: MonthWindow[];
  brokers: BrokerMonthsRow[];
  totals: Array<{ month: string; settled: Flow; submissions: Flow }>;
  bands: RemBand[];
  loanTypes: LoanTypeBreakdown;
  advisers: Array<{ adviserKey: string; adviserName: string; kind: BrokerMonthsKind }>;
  adviserFilter: string | null;
  bandsAreSeed: boolean;
  bandsInvalidReason?: string;
}

export type BrokerMonthlyResponse =
  | ({ status: 'ok' } & BrokerMonthlyPayload)
  | { status: 'no_snapshot'; asAtDate: string | null; reason: 'never_ingested' | 'not_committed' };

// ------------------------------------------------------------ pure helpers

/**
 * The band rules, mirrored from findBandProblems in rem/schema.ts on the server.
 *
 * Duplicated for the reason REPORT_LIMITS is: the browser cannot reach the functions package and
 * the isolation fence forbids a shared one. The server is the gate; this only lets the editor say
 * what is wrong before a round trip, in the same words.
 */
/** Mirrors MAX_REM_BAND_CENTS and MAX_REM_COST_CENTS in rem/schema.ts: $10 billion and $10 million. */
export const MAX_REM_BAND_CENTS = 1_000_000_000_000;
export const MAX_REM_COST_CENTS = 1_000_000_000;

export function findBandProblems(bands: readonly RemBand[]): Array<{ index: number; message: string }> {
  const problems: Array<{ index: number; message: string }> = [];
  bands.forEach((band, index) => {
    const label = `Band ${index + 1}`;
    if (!Number.isInteger(band.fromCents) || band.fromCents < 0 || (band.toCents !== null && (!Number.isInteger(band.toCents) || band.toCents < 0))) {
      problems.push({ index, message: `${label} needs whole-cent amounts of $0 or more` });
    }
    if (band.fromCents > MAX_REM_BAND_CENTS || (band.toCents !== null && band.toCents > MAX_REM_BAND_CENTS)) {
      problems.push({ index, message: `${label} cannot exceed $${(MAX_REM_BAND_CENTS / 100).toLocaleString('en-AU')}` });
    }
    if (!Number.isFinite(band.ratePct) || band.ratePct < 0 || band.ratePct > 100) {
      problems.push({ index, message: `${label} needs a rate from 0% to 100%` });
    }
    if (index === 0 && band.fromCents !== 0) {
      problems.push({ index, message: `${label} must start at $0, so every month's volume lands in a band` });
    }
    if (index > 0) {
      const previous = bands[index - 1];
      if (previous.toCents === null) {
        problems.push({ index, message: `${label} follows an open top band; only the last band may be open` });
      } else if (band.fromCents !== previous.toCents) {
        problems.push({
          index,
          message: band.fromCents > previous.toCents
            ? `${label} leaves a gap: it must start where band ${index} ends`
            : `${label} overlaps band ${index}: it must start where band ${index} ends`,
        });
      }
    }
    if (band.toCents !== null && band.toCents <= band.fromCents) {
      problems.push({ index, message: `${label} must end above where it starts` });
    }
  });
  return problems;
}

/**
 * A dollar amount typed by an operator, to integer cents. Null when it is not a non-negative
 * amount with at most two decimals. Parsed from the string, never via `* 100` on a float, so
 * "0.29" is 29 cents and not 28.999999.
 */
export function dollarsToCents(input: string): number | null {
  const cleaned = input.replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const [whole, fraction = ''] = cleaned.split('.');
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? cents : null;
}

/** Integer cents to the text an input shows. The inverse of dollarsToCents for whole-cent values. */
export function centsToDollarsInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '';
  const whole = Math.trunc(cents / 100);
  const part = Math.abs(cents % 100);
  return part === 0 ? String(whole) : `${whole}.${String(part).padStart(2, '0')}`;
}

/** '2026-09' to 'September 2026'. UTC so a label cannot slip a month. */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * '2026-09' to 'Sep 26', for column heads. A fixed list rather than the locale, whose en-AU short
 * form varies by runtime ('Sep' in one, 'Sept' in another), so a column head cannot change width
 * or wording between browsers.
 */
export function monthShort(month: string): string {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) return month;
  return `${SHORT_MONTHS[m - 1]} ${String(y).slice(-2)}`;
}

/** The last calendar day of 'YYYY-MM', as 'YYYY-MM-DD'. */
export function lastDayOfMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m, 0));
  return `${month}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/**
 * One month a board pack can be built for, and the two exports it is read from.
 *
 * WHY TWO EXPORTS. Practices upload weekly, so the last export inside a month is rarely on its
 * last day (29 Aug, then 5 Sep). Read only from 29 Aug, August would be a part month for ever and
 * never compared with July (stage, 2 Oct 2026). The two kinds of figure are therefore read where
 * each is true:
 *   - FLOW (settled, submitted) is dated: a deal settled in August carries an August date in every
 *     later export too. So it is read from `flowAsAtDate`, the FIRST export dated after the month
 *     ended, counting events dated from the 1st to the last day inclusive. That export sees the
 *     whole month and as little of the next as any export can.
 *   - STOCK (pipeline value, deals in flight) is a photograph and cannot be backdated. It is read
 *     from `asAtDate`, the LAST export on or before the month's last day, and the pack states
 *     that date.
 * With no export after the month yet, flow is read from `asAtDate` too, over the 1st to that date,
 * and the month is a part month (unless that export is on the last day, which already sees it all).
 */
export interface PackMonth {
  /** 'YYYY-MM'. */
  month: string;
  /** The last export on or before the month's last day: the month-end photograph. */
  asAtDate: string;
  /** The export settled and submitted are read from. Equal to `asAtDate` until a later one exists. */
  flowAsAtDate: string;
  /** True when `flowAsAtDate` sees the whole month: dated after it ended, or on its last day. */
  complete: boolean;
}

/**
 * The months a board pack can be built for: one per calendar month with an export in it, newest
 * first, each carrying the exports it is read from (see PackMonth). Pure: the same snapshot dates
 * always give the same months, whatever order they arrive in.
 */
export function packMonthsFromSnapshots(asAtDates: readonly string[]): PackMonth[] {
  const dates = [...new Set(asAtDates.filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)))].sort();
  const latest = new Map<string, string>();
  for (const date of dates) latest.set(date.slice(0, 7), date);
  return [...latest.entries()]
    .map(([month, asAtDate]) => {
      const last = lastDayOfMonth(month);
      const later = dates.find((d) => d > last);
      if (later !== undefined) return { month, asAtDate, flowAsAtDate: later, complete: true };
      return { month, asAtDate, flowAsAtDate: asAtDate, complete: asAtDate === last };
    })
    .sort((a, b) => (a.month < b.month ? 1 : a.month > b.month ? -1 : 0));
}

/**
 * The month just before the first export, read for its FLOW only, as a baseline.
 *
 * WHY. A tenant's history starts with its first upload. Evoke's first is 3 Sep 2026, so August had
 * no export dated inside it and no PackMonth, and September's pack read "No Aug 2026 export" on
 * settled and submitted (founder, 6 Oct 2026). But flow is dated: the 3 Sep export is the first
 * dated after August ended, which is exactly the export PackMonth reads a complete month's flow
 * from. So August's settled and submitted are known, on the same basis as any other month.
 *
 * Stock is not: there is no photograph of the book on or before 31 Aug, so `asAtDate` is empty
 * and the pipeline comparison stays unavailable, named as such.
 *
 * Only when the first export after the month is dated in the NEXT month. Read from an export a
 * year later, the filtered Performance export may no longer carry the month's deals, so a figure
 * would look complete and not be. Null when the month has an export inside it (it has a real
 * PackMonth) or no export follows it in time.
 */
export function flowOnlyPackMonth(month: string, asAtDates: readonly string[]): PackMonth | null {
  const dates = [...new Set(asAtDates.filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)))].sort();
  if (dates.some((d) => d.slice(0, 7) === month)) return null;
  const later = dates.find((d) => d > lastDayOfMonth(month));
  if (later === undefined || previousMonth(later.slice(0, 7)) !== month) return null;
  return { month, asAtDate: '', flowAsAtDate: later, complete: true };
}

/** The pack's window from the 1st to an export date (never past it). */
export function packWindow(asAtDate: string): { from: string; to: string } {
  return { from: `${asAtDate.slice(0, 7)}-01`, to: asAtDate };
}

/**
 * The range a pack's settled and submitted cover: the whole month when it is complete, else the
 * 1st to the month's last export. Always on or before `flowAsAtDate`, as a custom window must be.
 */
export function packFlowWindow(pack: Pick<PackMonth, 'month' | 'asAtDate' | 'complete'>): { from: string; to: string } {
  return { from: `${pack.month}-01`, to: pack.complete ? lastDayOfMonth(pack.month) : pack.asAtDate };
}

/**
 * The pack a reader lands on: the newest COMPLETE month, never the month still running.
 *
 * Complete is PackMonth's rule: an export dated after the month ended exists (or its last export is
 * on its last day). Only the newest month can lack a later export, so this is the newest month
 * when it is complete, else the one before it. The running month stays selectable; it is only not
 * the default, because a part month on arrival reads as a weak month (audit, 1 Oct 2026). With one
 * month of history it is the default.
 *
 * `packMonths` is packMonthsFromSnapshots' output, newest first.
 */
export function defaultPackMonth<T extends Pick<PackMonth, 'complete'>>(packMonths: readonly T[]): T | null {
  return packMonths.find((m) => m.complete) ?? packMonths[0] ?? null;
}

/** The month before 'YYYY-MM'. */
export function previousMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const index = y * 12 + (m - 1) - 1;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

// ------------------------------------------------------------ broker selector

/**
 * The broker selector's option values. "All brokers" and every person are namespaced, so no
 * adviser key can ever equal the "all" value. Before this, the Unassigned bucket's key was '',
 * the same value as "All brokers", and choosing it silently showed everyone.
 */
export const ALL_BROKERS_OPTION = 'all';
const PERSON_PREFIX = 'person:';

export const brokerOptionValue = (adviserKey: string): string => `${PERSON_PREFIX}${adviserKey}`;

/** The adviser key an option value selects, or '' for "All brokers" (and anything unrecognised). */
export function adviserKeyFromOption(value: string): string {
  return value.startsWith(PERSON_PREFIX) ? value.slice(PERSON_PREFIX.length) : '';
}
